import React, { useCallback, useState } from "react";

import { useForm } from "antd/es/form/Form";

import { Modal } from "@mapmycustomers/ui";

import CardType from "@app/enum/dashboard/CardType";
import CardConfiguration, { GoalConfiguration } from "@app/types/dashboard/CardConfiguration";
import DashboardCard from "@app/types/dashboard/DashboardCard";
import Scope from "@app/types/dashboard/Scope";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";

import EditCardForm, { FormValue } from "../components/EditCardForm";

import styles from "./EditCardModal.module.scss";
import Title from "./сomponents/Title";

interface Props {
  configuration: CardConfiguration<any, any, any>;
  details?: Pick<DashboardCard, "createdAt" | "createdBy" | "updatedAt" | "updatedBy">;
  onChange?: (
    configuration: CardConfiguration<any, any, any>,
    teamGoal?: GoalConfiguration
  ) => void;
  onHide: () => void;
  scope: Scope;
  teamGoal?: GoalConfiguration;
  type: CardType;
}

const EditCardModal: React.FC<Props> = ({
  configuration,
  details,
  onChange,
  onHide,
  scope,
  teamGoal,
  type,
}) => {
  const analytics = useExtendedAnalytics("Edit Card modal", useAnalytics());
  const [form] = useForm<FormValue>();

  const [currentConfiguration, setCurrentConfiguration] = useState(configuration);
  const [teamGoalConfiguration, setTeamGoalConfiguration] = useState(teamGoal);

  const handleFormChange = useCallback(
    ({ individualGoal, ...configuration }: FormValue) => {
      setCurrentConfiguration(configuration);
      setTeamGoalConfiguration((goal) => {
        if (goal && individualGoal !== undefined && scope.userId) {
          return {
            ...goal,
            goalType: configuration.goal?.goalType,
            individualUserGoals: {
              ...(goal?.individualUserGoals ?? {}),
              [scope.userId!]: individualGoal,
            },
          };
        }
      });
    },
    [scope]
  );

  const handleClose = useCallback(() => {
    // this is a naive comparison, but should be ok
    if (configuration !== currentConfiguration) {
      const { individualGoal, ...updatedConfiguration }: FormValue = form.getFieldsValue();

      const updatedTeamGoal = teamGoal;
      if (updatedTeamGoal?.enabled) {
        // if team goal is enabled, we cannot change current board goal anyway
        updatedConfiguration.goal = configuration.goal;
        if (individualGoal !== undefined && scope.userId) {
          updatedTeamGoal.individualUserGoals = {
            ...updatedTeamGoal.individualUserGoals,
            [scope.userId!]: individualGoal,
          };
        }
      }
      analytics.completed();
      onChange?.(updatedConfiguration, updatedTeamGoal);
    } else {
      analytics.cancelled();
    }
    onHide();
  }, [
    analytics,
    form,
    onHide,
    configuration,
    currentConfiguration,
    onChange,
    teamGoal,
    scope.userId,
  ]);

  return (
    <Modal
      className={styles.modal}
      footer={null}
      maskClosable={false}
      onCancel={handleClose}
      open
      title={<Title configuration={currentConfiguration} scope={scope} type={type} />}
      width="clamp(40rem, 60rem, 90vw)"
    >
      <EditCardForm
        className={styles.formContainer}
        configuration={configuration}
        details={details}
        form={form}
        onChange={handleFormChange}
        scope={scope}
        teamGoal={teamGoalConfiguration}
        type={type}
      />
    </Modal>
  );
};

export default EditCardModal;
